import React from "react";
import { Link } from "react-router-dom";

const CAPABILITIES = [
  {
    icon: "🧠",
    title: "AI Strategy & Readiness Assessment",
    description:
      "We audit your existing data assets, workflows, and team capabilities to produce a prioritised AI roadmap — identifying quick wins and long-term bets.",
    tags: ["Discovery", "Roadmapping", "Executive Workshops"],
  },
  {
    icon: "🔍",
    title: "Retrieval-Augmented Generation (RAG)",
    description:
      "Ground your LLM applications in your own knowledge base. We design and deploy RAG pipelines on top of your documents, wikis, and databases.",
    tags: ["LangChain", "LlamaIndex", "Pinecone", "pgvector"],
  },
  {
    icon: "🤖",
    title: "AI Agent Development",
    description:
      "Autonomous multi-step agents that use tools, browse the web, query APIs, and generate reports — reducing repetitive knowledge work at scale.",
    tags: ["OpenAI", "Claude", "Function Calling", "ReAct"],
  },
  {
    icon: "💬",
    title: "LLM-Powered Products & Chatbots",
    description:
      "Customer-facing AI assistants, internal copilots, and content generation pipelines built with enterprise-grade safety and observability.",
    tags: ["GPT-4o", "Prompt Engineering", "Guardrails", "LangSmith"],
  },
  {
    icon: "📊",
    title: "AI Analytics & Business Intelligence",
    description:
      "Natural language querying of your data warehouse, automated reporting, and anomaly detection — turning raw data into actionable insight.",
    tags: ["Text-to-SQL", "GA4", "BigQuery", "Python"],
  },
  {
    icon: "⚙️",
    title: "AI-Driven Process Automation",
    description:
      "Intelligent document processing, email triage, approval workflows, and data extraction — automating the manual steps that slow your teams down.",
    tags: ["n8n", "Zapier", "OCR", "Structured Outputs"],
  },
  {
    icon: "🔒",
    title: "Responsible AI & Governance",
    description:
      "Bias audits, explainability frameworks, data privacy reviews, and policy templates to ensure your AI deployments are ethical and compliant.",
    tags: ["GDPR", "Fairness", "Model Cards", "Audit Trails"],
  },
  {
    icon: "🚀",
    title: "AI Integration & MLOps",
    description:
      "Production deployment pipelines, model versioning, cost monitoring, and prompt regression testing so your AI stays reliable as it scales.",
    tags: ["OpenTelemetry", "CI/CD", "Langfuse", "Cost Governance"],
  },
];

const USE_CASES = [
  {
    industry: "Professional Services",
    icon: "🏛️",
    challenge: "Consultants spending hours manually summarising client documents and writing proposals.",
    solution: "RAG-powered knowledge assistant ingesting past proposals, industry reports, and client notes — surfacing relevant precedents in seconds.",
    outcome: "Proposal drafting time reduced; consultants focus on value-add analysis.",
  },
  {
    industry: "E-commerce & Retail",
    icon: "🛍️",
    challenge: "Support team overwhelmed with repetitive product and order questions.",
    solution: "LLM chatbot grounded in product catalogue, order history, and FAQs. Escalates to humans only for edge cases.",
    outcome: "High volume of tier-1 queries handled autonomously; higher agent satisfaction.",
  },
  {
    industry: "Marketing Agencies",
    icon: "📣",
    challenge: "Content production bottleneck — briefs to copy takes days across multiple stakeholders.",
    solution: "AI content pipeline: brief → SEO keyword research → first draft → tone-of-voice refinement → human polish.",
    outcome: "Content throughput increased; consistent brand voice across clients.",
  },
  {
    industry: "Financial Advisory",
    icon: "📈",
    challenge: "Advisors manually compiling market summaries and portfolio commentary every quarter.",
    solution: "Automated report generation pulling from market data APIs, blended with AI narrative generation and compliance checks.",
    outcome: "Quarterly reports produced per advisor in minutes instead of hours.",
  },
];

const PROCESS_STEPS = [
  {
    step: "01",
    title: "Discover",
    description: "Two-week discovery sprint: we map your data landscape, interview stakeholders, and identify the 3 highest-value AI opportunities.",
    color: "bg-indigo-600",
  },
  {
    step: "02",
    title: "Prototype",
    description: "Build a functional proof-of-concept in 4–6 weeks. Real data, real integrations — so you can validate impact before committing fully.",
    color: "bg-purple-600",
  },
  {
    step: "03",
    title: "Production",
    description: "Harden the prototype: observability, security review, user testing, and CI/CD pipeline. Deploy to production with confidence.",
    color: "bg-fuchsia-600",
  },
  {
    step: "04",
    title: "Scale & Iterate",
    description: "Ongoing optimisation: prompt tuning, cost governance, feature expansion, and model upgrades as the frontier advances.",
    color: "bg-pink-600",
  },
];

const TECH_STACK = [
  { name: "OpenAI GPT-4o", category: "LLM" },
  { name: "Anthropic Claude 3.5", category: "LLM" },
  { name: "Meta Llama 3", category: "Open Model" },
  { name: "LangChain", category: "Orchestration" },
  { name: "LlamaIndex", category: "Orchestration" },
  { name: "Pinecone", category: "Vector DB" },
  { name: "pgvector", category: "Vector DB" },
  { name: "LangSmith", category: "Observability" },
  { name: "Langfuse", category: "Observability" },
  { name: "Cloudflare Workers AI", category: "Edge Inference" },
  { name: "Stripe", category: "Billing" },
  { name: "n8n", category: "Automation" },
];

export default function AIServices() {
  return (
    <main className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-950 to-indigo-950 text-white pt-24 pb-24 px-4">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-indigo-500/15 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6 backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
            AI Services
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-none">
            Practical AI — not <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">science fair projects</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10">
            We help businesses move beyond AI hype to production systems that reduce costs, accelerate teams, 
            and create durable competitive advantage — built on battle-tested engineering practices.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/consulting"
              className="px-8 py-3.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold rounded-xl hover:opacity-90 transition shadow-xl"
            >
              Talk to Our AI Team →
            </Link>
            <Link
              to="/radar"
              className="px-8 py-3.5 border-2 border-white/30 text-white font-semibold rounded-xl hover:border-white/60 hover:bg-white/5 transition"
            >
              View Tech Radar
            </Link>
          </div>
        </div>
      </section>

      {/* ── Capabilities ── */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-slate-900 mb-4">AI Capabilities</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Eight distinct service lines — each delivering measurable ROI, not just experimentation.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {CAPABILITIES.map(cap => (
              <div key={cap.title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition group">
                <div className="text-3xl mb-4">{cap.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2 leading-snug">{cap.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">{cap.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {cap.tags.map(tag => (
                    <span key={tag} className="text-[10px] font-semibold bg-indigo-50 text-indigo-700 rounded-full px-2 py-0.5">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-slate-900 mb-4">How We Work</h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              A structured four-phase engagement that goes from idea to production — with no wasted cycles.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS_STEPS.map(s => (
              <div key={s.step} className="relative">
                <div className={`w-12 h-12 ${s.color} rounded-2xl flex items-center justify-center text-white font-black text-lg mb-4 shadow-lg`}>
                  {s.step}
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-black text-slate-900 mb-4">Real-World Applications</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Illustrative scenarios across industries — showing where AI creates the most tangible value.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {USE_CASES.map(uc => (
              <div key={uc.industry} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{uc.icon}</span>
                  <h3 className="font-bold text-gray-900">{uc.industry}</h3>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-rose-600 mb-1">Challenge</p>
                    <p className="text-gray-600">{uc.challenge}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-600 mb-1">Solution</p>
                    <p className="text-gray-600">{uc.solution}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600 mb-1">Outcome</p>
                    <p className="text-gray-700 font-medium">{uc.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section className="py-16 px-4 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl font-black text-slate-900 mb-2">Our AI Technology Stack</h2>
          <p className="text-slate-500 text-sm mb-10">Best-in-class tools, selected for reliability and production-readiness.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {TECH_STACK.map(t => (
              <div key={t.name} className="flex items-center gap-2 bg-slate-50 border border-gray-200 rounded-xl px-4 py-2.5">
                <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">{t.category}</span>
                <span className="w-px h-3 bg-gray-300" />
                <span className="text-sm font-semibold text-gray-800">{t.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-black text-slate-900 mb-10 text-center">Common Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "We don't have a data science team — can we still benefit from AI?",
                a: "Absolutely. Most of our clients are domain experts, not AI specialists. We bring the technical depth; you bring the business context. We design systems your existing teams can operate without specialist ML knowledge.",
              },
              {
                q: "How do you handle data privacy with LLM APIs?",
                a: "We offer deployment options that never send sensitive data to third-party LLM APIs — using on-premise or Cloudflare Workers AI inference for privacy-critical workloads. We also review data handling agreements with providers like OpenAI for enterprise tiers.",
              },
              {
                q: "What's a realistic timeline to see value?",
                a: "Our discovery sprint (2 weeks) produces a concrete roadmap. The prototype phase (4–6 weeks) delivers something you can demo and measure. Most clients see a production-ready system within 3–4 months of engagement start.",
              },
              {
                q: "How do we avoid getting locked into one AI vendor?",
                a: "We design abstraction layers (LangChain / LlamaIndex) that allow model swapping without rewriting application logic. Your business logic, prompts, and data pipelines remain vendor-neutral.",
              },
              {
                q: "Can AI automate our internal processes without replacing staff?",
                a: "Yes — and that's the right frame. Our engagements focus on augmentation: AI handles the repetitive, time-consuming tasks while your team focuses on judgment, relationships, and creative work.",
              },
            ].map(faq => (
              <details key={faq.q} className="bg-white rounded-xl border border-gray-200 group">
                <summary className="flex justify-between items-start px-5 py-4 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 pr-4 text-sm">{faq.q}</span>
                  <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-gradient-to-br from-purple-600 to-indigo-700 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-4">Ready to put AI to work?</h2>
          <p className="text-purple-100 text-lg mb-8 max-w-2xl mx-auto">
            Start with a no-pressure discovery conversation. We'll identify your highest-value AI opportunity in one hour.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/consulting"
              className="px-8 py-3.5 bg-white text-purple-700 font-bold rounded-xl hover:bg-purple-50 transition shadow-lg"
            >
              Book a Discovery Call →
            </Link>
            <Link
              to="/radar"
              className="px-8 py-3.5 border-2 border-white/40 text-white font-semibold rounded-xl hover:border-white/70 hover:bg-white/10 transition"
            >
              Explore the Tech Radar
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
